import { Capacitor } from '@capacitor/core';
import { App as CapApp } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { supabase } from './supabaseClient';
import { setSessionToken } from './apiClient';
import { emitInternalNavigation } from './internalNavigate';

const CUSTOM_SCHEME_PREFIX = 'skipdq://';

/**
 * Parse skipdq://auth/callback?code=... (PKCE — exchange in main WebView where code_verifier lives)
 * or #access_token=...&refresh_token=... (legacy handoff).
 */
function parseFoodtruckOAuthUrl(url) {
  if (!url || typeof url !== 'string' || !url.startsWith(CUSTOM_SCHEME_PREFIX)) {
    return null;
  }

  const q = url.indexOf('?');
  const h = url.indexOf('#');
  if (q !== -1) {
    const queryEnd = h !== -1 && h > q ? h : url.length;
    const params = new URLSearchParams(url.slice(q + 1, queryEnd));
    const code = params.get('code');
    if (code) {
      return { kind: 'code', code };
    }
  }

  if (h !== -1) {
    const params = new URLSearchParams(url.slice(h + 1));
    const access_token = params.get('access_token');
    const refresh_token = params.get('refresh_token');
    if (access_token && refresh_token) {
      return { kind: 'tokens', access_token, refresh_token };
    }
  }

  return null;
}

async function applyParsedSession(parsed) {
  if (parsed.kind === 'code') {
    const { data, error } = await supabase.auth.exchangeCodeForSession(parsed.code);
    if (error || !data?.session?.access_token) {
      console.error('[appUrlOpen] exchangeCodeForSession failed', error);
      return false;
    }
    setSessionToken(data.session.access_token);
    return true;
  }

  const { data, error } = await supabase.auth.setSession({
    access_token: parsed.access_token,
    refresh_token: parsed.refresh_token,
  });
  if (error) {
    console.error('[appUrlOpen] setSession failed', error);
    return false;
  }
  const at = data?.session?.access_token ?? parsed.access_token;
  setSessionToken(at);
  return true;
}

/**
 * Register before React mounts. OAuth completes on Vercel in SFSafariViewController; we forward the
 * auth code to the app so exchange runs in the main WebView (PKCE verifier is only there).
 */
export function registerNativeOauthDeepLinkListener() {
  if (!Capacitor.isNativePlatform()) return;
  if (!supabase) return;

  const g = typeof globalThis !== 'undefined' ? globalThis : window;
  if (g.__nativeOauthDeepLinkRegistered) return;
  g.__nativeOauthDeepLinkRegistered = true;

  CapApp.addListener('appUrlOpen', async ({ url }) => {
    console.log('[appUrlOpen] native OAuth deep link', url);
    const parsed = parseFoodtruckOAuthUrl(url);
    if (!parsed) return;

    const ok = await applyParsedSession(parsed);
    if (!ok) return;

    try {
      await Browser.close();
    } catch (e) {
      console.warn('[appUrlOpen] Browser.close', e);
    }

    window.setTimeout(() => emitInternalNavigation('/'), 0);
  });
}
